import { ModalGeneric } from 'ypf-pas-general-ui-components';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';

import {
  getProgramPoints,
  getProgramPointsWithPPFijado,
  postProgramPoint,
  rectiyProgramPoint
} from '../../../services/workReport';
import { workReportLineStatus } from '../../../utils/dataStatus';
import useToast from '../../../hooks/useToast';

import './ProgramPointModal.scss';

import ProgramPointList from './ProgramPointList/ProgramPointList';

const mapProgramPointFixed = (
  {
    puntoDeProgramaNotificado,
    cantidadNotificada,
    cantidadFijada,
    puntoDeProgramaFijado,
    id,
    idParteTrabajoDiario,
    claseCosto,
    codigo,
    subcodigo,
    descripcion
  },
  idParteDiario
) => ({
  quantity: cantidadNotificada || '',
  points: puntoDeProgramaNotificado || '',
  quantityFijados: cantidadFijada || '',
  pointsFijados: puntoDeProgramaFijado || '',
  idPartePuntoPrograma: id,
  idParteDiario: idParteTrabajoDiario || idParteDiario,
  cost: claseCosto,
  code: codigo,
  subCode: subcodigo,
  detail: descripcion
});

const mapProgramPoint = (
  {
    puntoDePrograma,
    cantidad,
    id,
    idParteTrabajoDiario,
    claseCosto,
    codigo,
    subcodigo,
    descripcion
  },
  idParteDiario
) => ({
  quantity: cantidad || '',
  points: puntoDePrograma || '',
  idPartePuntoPrograma: id,
  idParteDiario: idParteTrabajoDiario || idParteDiario,
  cost: claseCosto
    ? {
        costoClase: claseCosto?.split('-')[1],
        id: claseCosto,
        nombre: claseCosto,
        label: claseCosto,
        fase: claseCosto?.split('-')[1]
      }
    : null,
  code: claseCosto ? { nombre: codigo, id: codigo, label: codigo } : null,
  subCode: subcodigo ? { nombre: subcodigo, id: subcodigo, label: subcodigo } : null,
  detail: descripcion
});

const ProgramPointModalFormik = ({ show, onHide, ...props }) => {
  const { setNewToast } = useToast();
  const {
    line,
    disabledPPModal,
    tienePuntosPrograma,
    setIsRectifyAmount,
    isRectifyAmount,
    selectedDate,
    setNewFetch
  } = props;

  const [loading, setLoading] = useState(false);
  const [quantityTotalReturn, setQuantityTotalReturn] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const { values, setFieldValue, resetForm } = useFormik({
    initialValues: {
      programPoints: [
        {
          quantity: '',
          points: '',
          idPartePuntoPrograma: null,
          idParteDiario: null
        }
      ],
      totalValue: 0
    }
  });

  const onExit = () => {
    resetForm();
    setIsRectifyAmount(false);
    setNewFetch((oldValue) => !oldValue);
    onHide();
  };

  useEffect(() => {
    const getDataPP = async () => {
      try {
        setLoading(true);

        let res;
        let totalValue;

        if (line.estadoEnum === workReportLineStatus.fijar) {
          res = await getProgramPointsWithPPFijado(line?.idParteDiario);

          const adapterPP = res?.map((pp) => mapProgramPointFixed(pp, line?.idParteDiario));

          totalValue = adapterPP?.reduce((acc, curr) => acc + Number(curr.quantity), 0) || 0;
          const totalValueFijado =
            adapterPP?.reduce((acc, curr) => acc + Number(curr.quantityFijados), 0) || 0;

          setFieldValue('programPoints', adapterPP);
          setQuantityTotalReturn(totalValueFijado);
        } else {
          res = await getProgramPoints(line?.idParteDiario);

          const adapterPP = res?.map((pp) => mapProgramPoint(pp, line?.idParteDiario));

          totalValue = adapterPP?.reduce((acc, curr) => acc + Number(curr.quantity), 0) || 0;
          setFieldValue(
            'programPoints',
            adapterPP?.length === 0
              ? [
                  {
                    quantity: '',
                    points: ''
                  }
                ]
              : adapterPP
          );
          setQuantityTotalReturn(totalValue);
        }
        setFieldValue('totalValue', totalValue);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        setNewToast({
          type: 'ERROR',
          message:
            error?.data?.errorMessage || 'No pudimos procesar la solicitud, intente nuevamente.'
        });
      }
    };

    if (show && tienePuntosPrograma) getDataPP();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handlePostProgramPoints = async (id, body) => {
    try {
      setLoading(true);
      let res;

      if (isRectifyAmount) {
        const dataToSendRectify = {
          parteDiarioPuntosPrograma: body?.map(
            ({ quantity, points, idPartePuntoPrograma, idParteDiario }) => ({
              puntosDePrograma: points === '' ? null : Number(points),
              cantidad: Number(quantity),
              idPartePuntoPrograma: idPartePuntoPrograma || 0,
              idParteDiario: idParteDiario || line?.idParteDiario
            })
          )
        };

        res = await rectiyProgramPoint(id, dataToSendRectify);
      } else {
        const dataToSend = body?.map(
          ({ quantity, points, idPartePuntoPrograma, idParteDiario }) => ({
            puntosDePrograma: points === '' ? null : Number(points),
            cantidad: Number(quantity),
            idPartePuntoPrograma: idPartePuntoPrograma || 0,
            idParteDiario: idParteDiario || line?.idParteDiario
          })
        );

        res = await postProgramPoint(id, dataToSend);
      }

      setNewToast({
        type: 'SUCCESS',
        message: res || 'Se ha creado  correctamente'
      });
      onExit();
      setLoading(false);
    } catch (error) {
      onExit();
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message:
          error?.data?.errorMessage || 'No pudimos procesar la solicitud, intente nuevamente.'
      });
    }
  };

  const disabledToEdit =
    (line?.estadoEnum === workReportLineStatus.fijar && tienePuntosPrograma) ||
    (line?.estadoEnum === workReportLineStatus.aprobar && tienePuntosPrograma);

  const disabledInput =
    !isRectifyAmount &&
    line?.estadoEnum !== workReportLineStatus.pendiente &&
    line?.estadoEnum !== workReportLineStatus.devolver &&
    line?.estadoEnum !== workReportLineStatus.enviar;

  const disabledNotifyWithPPwithoutRect =
    line?.estadoEnum === workReportLineStatus.enviar && tienePuntosPrograma && !isRectifyAmount;

  useEffect(() => {
    const hasCompleteData = values.programPoints.every(
      (PP) =>
        Object.prototype.hasOwnProperty.call(PP, 'code') &&
        Object.prototype.hasOwnProperty.call(PP, 'subCode') &&
        Object.prototype.hasOwnProperty.call(PP, 'cost')
    );

    if (hasCompleteData) {
      setShowAlert(
        values.programPoints.some(
          (programPoint) =>
            programPoint?.code === null ||
            programPoint?.code === '' ||
            programPoint?.subCode === null ||
            programPoint?.subCode === '' ||
            programPoint?.cost === null ||
            programPoint?.cost === '' ||
            programPoint?.cost?.nombre === '' ||
            programPoint?.code?.nombre === '' ||
            programPoint?.subCode?.nombre === ''
        )
      );
    } else {
      setShowAlert(true);
    }
  }, [values.programPoints]);

  return (
    <ModalGeneric
      backdrop
      infoModal
      onHide={onExit}
      primaryAction={() => {
        handlePostProgramPoints(line?.idParteDiario, values?.programPoints);
      }}
      primaryActionDisabled={
        values?.programPoints?.find((qntyObj) => !qntyObj.quantity) ||
        disabledPPModal ||
        disabledToEdit ||
        disabledNotifyWithPPwithoutRect ||
        values?.programPoints?.find(
          (qntyObj, index) => qntyObj.quantity && !qntyObj.points && index !== 0
        )
      }
      primaryActionLabel="Enviar a Aprobar"
      secondaryAction={onExit}
      secondaryActionLabel="Salir"
      show={show}
      size="xl"
      title="Editar Puntos de Programa"
    >
      <ProgramPointList
        dailyView
        disabledButton={disabledInput || disabledNotifyWithPPwithoutRect}
        disabledInputPP={
          disabledInput || disabledPPModal || disabledToEdit || disabledNotifyWithPPwithoutRect
        }
        disabledInputQuantity={
          disabledInput || disabledToEdit || disabledPPModal || disabledNotifyWithPPwithoutRect
        }
        displayButtons={
          !disabledToEdit && !disabledPPModal && !disabledInput && !disabledNotifyWithPPwithoutRect
        }
        displayFixedlabel
        idParteDiario={line?.idParteDiario}
        line={line}
        loading={loading}
        quantityTotalReturn={quantityTotalReturn}
        selectedDate={selectedDate}
        setFieldValue={setFieldValue}
        showAlert={showAlert}
        showTitle
        tipoAsignacion={line?.tipoAsignacion}
        values={values}
        viewCSA
        viewInputs
      />
    </ModalGeneric>
  );
};

ProgramPointModalFormik.displayName = 'ProgramPointModalFormik';
ProgramPointModalFormik.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  line: PropTypes.shape({
    positionName: PropTypes.string,
    lineName: PropTypes.string,
    idParteDiario: PropTypes.number,
    estadoEnum: PropTypes.number,
    tienePuntosPrograma: PropTypes.bool,
    tipoAsignacion: PropTypes.number
  }),
  amount: PropTypes.number,
  setAmount: PropTypes.func,
  disabledPPModal: PropTypes.bool,
  setIsRectifyAmount: PropTypes.func,
  isRectifyAmount: PropTypes.bool,
  tienePuntosPrograma: PropTypes.bool,
  selectedDate: PropTypes.string,
  setNewFetch: PropTypes.func
};

ProgramPointModalFormik.defaultProps = {
  amount: 0,
  setAmount: () => {},
  line: {},
  tienePuntosPrograma: false,
  disabledPPModal: false,
  setIsRectifyAmount: () => {},
  isRectifyAmount: false,
  selectedDate: '',
  setNewFetch: () => {}
};
export default ProgramPointModalFormik;
